// app/src/solana/idlDecode.ts
import { BorshAccountsCoder, type Idl } from "@coral-xyz/anchor";
import { Buffer } from "buffer";
import idlJson from "../idl/weare_susu.json";

const idl = idlJson as unknown as Idl;
const coder = new BorshAccountsCoder(idl);

function eq8(a: Uint8Array, b: number[]): boolean {
  if (a.length !== 8 || b.length !== 8) return false;
  for (let i = 0; i < 8; i++) if (a[i] !== b[i]) return false;
  return true;
}

function toPlain(value: any): any {
  if (value && typeof value === "object") {
    if (typeof value.toBase58 === "function") return value.toBase58();
    // BN-like
    if (typeof value.toString === "function" && (value.words || value.negative !== undefined)) {
      return value.toString(10);
    }
    if (typeof value === "bigint") return value.toString(10);
    if (value instanceof Uint8Array) return Array.from(value);
    if (Array.isArray(value)) return value.map(toPlain);

    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(value)) out[k] = toPlain(v);
    return out;
  }
  return value;
}

export type IdlDecodeResult =
  | { ok: true; accountName: string; decoded: any }
  | { ok: false; reason: string; discriminator?: number[] };

export function decodeAccountByIdl(data: Uint8Array): IdlDecodeResult {
  try {
    if (!data || data.length < 8) {
      return { ok: false, reason: "Account data is empty or too short (< 8 bytes)." };
    }

    const disc = data.slice(0, 8);
    const discArr = Array.from(disc).map((n) => Number(n));

    const accounts = (idlJson as any).accounts ?? [];
    for (const a of accounts) {
      const expected = a.discriminator as number[] | undefined;
      if (!expected || expected.length !== 8) continue;
      if (!eq8(disc, expected)) continue;

      const decoded = coder.decode(a.name, Buffer.from(data));
      const plain = toPlain(decoded);
      return { ok: true, accountName: a.name, decoded: plain };
    }

    return {
      ok: false,
      reason: "No IDL discriminator matched this account data (IDL mismatch or non-Anchor account).",
      discriminator: discArr,
    };
  } catch (e: any) {
    return { ok: false, reason: e?.message ? String(e.message) : String(e) };
  }
}
